import { create } from "zustand";
import { persist } from "zustand/middleware";

interface AdminUser {
  id: number;
  email: string;
  username: string;
  role: string;
  totpEnabled: boolean;
}

interface AdminAuthState {
  token: string | null;
  refreshToken: string | null;
  admin: AdminUser | null;
  isAuthenticated: boolean;
  pendingTwoFactor: boolean;
  tempToken: string | null;
  tokenExpiresAt: number | null;
  
  setAuth: (token: string, refreshToken: string, admin: AdminUser) => void;
  setPending2FA: (tempToken: string) => void;
  logout: () => void;
  getAuthHeader: () => Record<string, string>;
  refreshAccessToken: () => Promise<boolean>;
}

export const useAdminAuth = create<AdminAuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      admin: null,
      isAuthenticated: false,
      pendingTwoFactor: false,
      tempToken: null,
      tokenExpiresAt: null,

      setAuth: (token: string, refreshToken: string, admin: AdminUser) => {
        // Calculate token expiration (default 15 minutes)
        const expiresIn = 15 * 60 * 1000; // 15 minutes
        const tokenExpiresAt = Date.now() + expiresIn;
        
        set({
          token,
          refreshToken,
          admin,
          isAuthenticated: true,
          pendingTwoFactor: false,
          tempToken: null,
          tokenExpiresAt,
        });
      },

      setPending2FA: (tempToken: string) => {
        set({
          pendingTwoFactor: true,
          tempToken,
          isAuthenticated: false,
        });
      },

      logout: () => {
        // Call server to revoke session
        const { refreshToken } = get();
        if (refreshToken) {
          fetch("/api/admin/logout", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
          }).catch(() => {}); // Ignore errors
        }
        
        set({
          token: null,
          refreshToken: null,
          admin: null,
          isAuthenticated: false,
          pendingTwoFactor: false,
          tempToken: null,
          tokenExpiresAt: null,
        });
      },

      getAuthHeader: (): Record<string, string> => {
        const { token } = get();
        if (token) {
          return { Authorization: `Bearer ${token}` };
        }
        return {};
      },
      
      refreshAccessToken: async (): Promise<boolean> => {
        const { refreshToken } = get();
        if (!refreshToken) return false;
        
        try {
          const response = await fetch("/api/admin/refresh-token", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refreshToken }),
          });
          
          if (response.ok) {
            const data = await response.json();
            set({
              token: data.token,
              refreshToken: data.refreshToken,
              tokenExpiresAt: Date.now() + 15 * 60 * 1000,
            });
            return true;
          }
          
          // If refresh failed, logout
          get().logout();
          return false;
        } catch (error) {
          console.error("Token refresh failed:", error);
          get().logout();
          return false;
        }
      },
    }),
    {
      name: "admin-auth",
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        admin: state.admin,
        isAuthenticated: state.isAuthenticated,
        tokenExpiresAt: state.tokenExpiresAt,
      }),
    }
  )
);

export async function adminFetch(url: string, options: RequestInit = {}) {
  const { getAuthHeader, logout, refreshAccessToken, tokenExpiresAt } = useAdminAuth.getState();
  
  // Check if token is about to expire (within 2 minutes)
  if (tokenExpiresAt && Date.now() > tokenExpiresAt - 2 * 60 * 1000) {
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      logout();
      window.location.href = "/admin/login";
      throw new Error("Session expired");
    }
  }
  
  const response = await fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...getAuthHeader(),
      ...options.headers,
    },
  });

  if (response.status === 401) {
    // Try to refresh token on 401
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      // Retry the request with new token
      const retryResponse = await fetch(url, {
        ...options,
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeader(),
          ...options.headers,
        },
      });
      
      if (retryResponse.status === 401) {
        logout();
        window.location.href = "/admin/login";
        throw new Error("Session expired");
      }
      
      return retryResponse;
    }
    
    logout();
    window.location.href = "/admin/login";
    throw new Error("Session expired");
  }

  return response;
}
